import pc from "picocolors";
import { AppErrorReason } from "~errors/errors";
import { KeyStatRow, QuoteData } from "~types/types";
import { TranslateMessageFn } from "~utils/i18n/translate";
import { HIGH_PRECISION_SECURITY_TYPES } from "~constants";

const { NODE_ENV } = process.env;

export const delay = (waitTime: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
      resolve(true);
    }, waitTime);
  });
};

export function createError(reason: AppErrorReason, context: string): Error {
  if (NODE_ENV === "development") {
    return new Error(
      `\n\n${pc.bgYellow(pc.black(`${reason}: ${context}`))}\n\n`
    );
  }

  return new Error(`${reason}: ${context}`);
}

export function ensureStringContains(str: string, suffix: string) {
  if (!str || str.toLowerCase().lastIndexOf(suffix.toLowerCase()) > -1) {
    return str;
  }

  return `${str} ${suffix}`;
}

export function getDescriptionAndTitle(
  type: string,
  tickerName: string,
  tickerId: string,
  primaryExchange: string,
  securityName: string,
  mediaSecuritySubtype: string
) {
  const ticker = shortTicker(tickerId.toString()).toUpperCase();
  let title = `${ticker} Analysis - ${tickerName} - Bloomberg`;

  const tickerNameWithIndex = ensureStringContains(tickerName, "Index");
  const tickerNameWithFund = ensureStringContains(tickerName, "Fund");

  let description = "";
  let descriptionName = `${tickerName} (${ticker})`;

  const tickerPlusExchange = primaryExchange
    ? `${ticker}:${primaryExchange}`
    : tickerId;
  const exchange = primaryExchange || "";
  let constructedTitle = null;

  switch (type) {
    case "INDF":
    case "IND":
    case "INDS":
    case "INDN":
      title = `${ticker} Quote - ${tickerNameWithIndex}`;
      // title tends to be slightly different (in shichang) when mediaSecuritySubtype is Equity. There exists two slightly different titles in this case
      constructedTitle =
        mediaSecuritySubtype?.toLowerCase() === "equity"
          ? `${ticker} Quote - ${tickerName} - Bloomberg`
          : null;
      description = `Index performance for ${tickerName} (${ticker}) including value, chart, profile & other market data.`;
      break;
    case "ETF":
    case "ETP": {
      title = `${ticker} Quote - ${tickerNameWithFund} - Bloomberg`;
      descriptionName = `${tickerName} (${ticker} - Type ETF)`;
      description = formDefaultDescription(descriptionName);
      break;
    }
    case "CUR": {
      const [firstName, secondName] = securityName.split("/");
      const firstTicker = ticker.slice(0, 3);
      const secondTicker = ticker.slice(3, 7);
      const isTwoPartTicker = firstName && secondName && ticker.length === 6;

      const primaryCurrencyTechnicalName = `${firstName} (${firstTicker})`;
      const secondaryCurrencyTechnicalName = `${secondName} (${secondTicker})`;

      title = selectMetaValue(
        isTwoPartTicker as boolean,
        `${firstTicker} to ${secondTicker} Exchange Rate`,
        `${firstName} (${ticker}) Spot Rate`
      );

      description = selectMetaValue(
        isTwoPartTicker as boolean,
        `Current exchange rate ${primaryCurrencyTechnicalName} to ${secondaryCurrencyTechnicalName} including currency converter, buying & selling rate and historical conversion chart.`,
        `Updated spot exchange rate of ${primaryCurrencyTechnicalName} against the US dollar index. Find currency & selling price and other forex information`
      );
      break;
    }
    case "COM": {
      constructedTitle = `${ticker} Quote - ${tickerName} - Bloomberg`;
      title = `${ticker} Commodity Quote - ${tickerName}`;
      description = `Stock analysis for ${ticker}. Get stock price, historical stock charts & news for ${tickerName}`;
      break;
    }
    case "MMF": {
      title = `${ticker} Analysis - ${tickerNameWithFund} - Bloomberg`;
      descriptionName = `${tickerNameWithFund} (${ticker} - Type MMF)`;
      description = formDefaultDescription(descriptionName);
      break;
    }
    case "COR":
    case "GOV": {
      description = `Analysis & trends for ${descriptionName} including current rate, chart, profile, news and other data.`;
      break;
    }
    case "CEFH":
    case "CEF": {
      title = `${ticker} Analysis - ${tickerNameWithFund} - Bloomberg`;
      descriptionName = `${tickerNameWithFund} (${ticker})`;
      description = formDefaultDescription(descriptionName);
      break;
    }
    case "OEF":
    case "OEFH": {
      title = `${ticker} Quote - ${tickerNameWithFund} - Bloomberg`;
      descriptionName = `${tickerNameWithFund} (${ticker})`;
      description = formDefaultDescription(descriptionName);
      break;
    }
    case "EQT":
    default: {
      title = `${ticker}: ${tickerName} Stock Price Quote - ${exchange} - Bloomberg`;
      descriptionName = `${tickerName} (${tickerPlusExchange})`;
      description = `Stock analysis for ${descriptionName} including stock price, stock chart, company news, key statistics, fundamentals and company profile.`;
    }
  }

  return { title, description, constructedTitle };
}

function formDefaultDescription(descriptionName: string) {
  return `Performance charts for ${descriptionName} including intraday, historical and comparison charts, technical analysis and trend lines.`;
}

export function shortTicker(tickerId: string) {
  if (typeof tickerId !== "string") {
    return "";
  }

  return decodeURIComponent(tickerId.split(":")[0]);
}

function selectMetaValue(
  isTwoPartTicker: boolean,
  doubleMetaValue: string,
  singleMetaValue: string
) {
  return isTwoPartTicker ? doubleMetaValue : singleMetaValue;
}

function formatNetAssetValueLabel(date: string | null, translateMessage: any) {
  if (!date) return "--";
  const labelInfo = { id: "navOn$date", defaultMessage: `Nav(On ${date})` };
  const labelValues = { netAssetValueDate: date };

  return translateMessage(labelInfo, labelValues);
}

function formatTotalAssetsLabel(
  date: string | null,
  currency: number | null,
  translateMessage: any
) {
  if (!date) return "--";
  const safeCurrency = currency || "USD";
  const labelInfo = {
    id: "totalAssets$currency$date",
    defaultMessage: `Total Assets (M ${safeCurrency}) (On ${date})`,
  };
  const labelValues = {
    totalAssetsCurrency: safeCurrency,
    totalAssetsDate: date,
  };

  return translateMessage(labelInfo, labelValues);
}

export function getKeyStatistics(
  quote: QuoteData,
  translateMessage: TranslateMessageFn
) {
  const {
    type,
    priceEarningsRatio,
    priceEarningsToGrowthAndDividendYieldRatio,
    sharesOutstanding,
    priceToBookRatio,
    priceToSalesRatio,
    averageVolume30Day,
    earningsPerShare,
    dividend,
    lastDividendReported,
    averageDaysToMaturity,
    percentPremium,
    totalReturn3Month,
    totalReturnYtd,
    totalReturn1Year,
    totalReturn3Year,
    totalReturn5Year,
    netAssetValue,
    totalAssets,
    inceptionDate,
    percentPremium52WeekAverage,
    indicatedGrossDividendYield,
    frontLoadFee,
    backLoadFee,
    redemptionFee,
    currentManagementFee,
    expenseRatio,
    netAssetValueDate,
    totalAssetsDate,
    totalAssetsCurrency,
    fundMarketingFee,
  } = quote;

  let allKeyStatistics: KeyStatRow[] = [];

  if (type === "EQT" || type === "IND" || type === "INDS") {
    allKeyStatistics = [
      {
        key: translateMessage({
          id: "pERatio",
          defaultMessage: "P/E Ratio",
        }),
        value: priceEarningsRatio,
      },
      {
        key: translateMessage({
          id: "pegyRatio",
          defaultMessage: "PEGY Ratio",
        }),
        value: priceEarningsToGrowthAndDividendYieldRatio,
      },
      {
        key: translateMessage({
          id: "sharesOutstanding",
          defaultMessage: "Shares Outstanding",
        }),
        value: sharesOutstanding,
      },
      {
        key: translateMessage({
          id: "priceToBookRatio",
          defaultMessage: "Price to Book Ratio",
        }),
        value: priceToBookRatio,
      },
      {
        key: translateMessage({
          id: "priceToSalesRatio",
          defaultMessage: "Price to Sales Ratio",
        }),
        value: priceToSalesRatio,
      },
      {
        key: translateMessage({
          id: "1YearReturn",
          defaultMessage: "1 Year Return",
        }),
        value: totalReturn1Year,
      },
      {
        key: translateMessage({
          id: "30DayAvgVolume",
          defaultMessage: "30 Day Avg Volume",
        }),
        value: averageVolume30Day,
      },
      {
        key: translateMessage({ id: "eps", defaultMessage: "EPS" }),
        value: earningsPerShare,
      },
      {
        key: translateMessage({ id: "dividend", defaultMessage: "Dividend" }),
        value: dividend,
      },
      {
        key: translateMessage({
          id: "lastDividendReported",
          defaultMessage: "Last Dividend Reported",
        }),
        value: lastDividendReported,
      },
    ];
  } else if (
    type === "ETP" ||
    type === "OEF" ||
    type === "CEF" ||
    type === "CEFH" ||
    type === "MMF"
  ) {
    allKeyStatistics = [
      {
        key: translateMessage({
          id: "daysToMaturity",
          defaultMessage: "Days To Maturity",
        }),
        value: averageDaysToMaturity,
      },
      {
        key: translateMessage({
          id: "3MonthReturn",
          defaultMessage: "3 Month Return",
        }),
        value: totalReturn3Month,
      },
      {
        key: translateMessage({
          id: "ytdReturn",
          defaultMessage: "YTD Return",
        }),
        value: totalReturnYtd,
      },
      {
        key: translateMessage({
          id: "1YearReturn",
          defaultMessage: "1 Year Return",
        }),
        value: totalReturn1Year,
      },
      {
        key: translateMessage({
          id: "3YearReturn",
          defaultMessage: "3 Year Return",
        }),
        value: totalReturn3Year,
      },
      {
        key: translateMessage({
          id: "5YearReturn",
          defaultMessage: "5 Year Return",
        }),
        value: totalReturn5Year,
      },
      {
        key: formatNetAssetValueLabel(netAssetValueDate, translateMessage),
        value: netAssetValue,
      },
      {
        key: formatTotalAssetsLabel(
          totalAssetsDate,
          totalAssetsCurrency,
          translateMessage
        ),
        value: totalAssets,
      },
      {
        key: translateMessage({
          id: "inceptionDate",
          defaultMessage: "Inception Date",
        }),
        value: inceptionDate || "--",
      },
      {
        key: translateMessage({
          id: "premiumDiscount",
          defaultMessage: "Premium/Discount",
        }),
        value: percentPremium,
      },
      {
        key: translateMessage({
          id: "average52WeekPremium",
          defaultMessage: "Average 52-week Premium",
        }),
        value: percentPremium52WeekAverage,
      },
      {
        key: translateMessage({
          id: "lastDividendReported",
          defaultMessage: "Last Dividend Reported",
        }),
        value: lastDividendReported,
      },
      {
        key: translateMessage({
          id: "dividendIndicatedGrossYield",
          defaultMessage: "Dividend Indicated Gross Yield",
        }),
        value: indicatedGrossDividendYield,
      },
      {
        key: translateMessage({
          id: "frontLoadFee",
          defaultMessage: "Front Load Fee",
        }),
        value: frontLoadFee,
      },
      {
        key: translateMessage({
          id: "backLoadFee",
          defaultMessage: "Back Load Fee",
        }),
        value: backLoadFee,
      },
      {
        key: translateMessage({
          id: "redemptionFee",
          defaultMessage: "Redemption Fee",
        }),
        value: redemptionFee,
      },
      {
        key: translateMessage({ id: "12b1Fee", defaultMessage: "12B-1 Fee" }),
        value: fundMarketingFee,
      },
      {
        key: translateMessage({
          id: "currentMgmtFee",
          defaultMessage: "Current Mgmt Fee",
        }),
        value: currentManagementFee,
      },
      {
        key: translateMessage({
          id: "expenseRatio",
          defaultMessage: "Expense Ratio",
        }),
        value: expenseRatio,
      },
    ];
  }

  return allKeyStatistics;
}

export const showKeyStastistic = (quote: QuoteData) => {
  const { type } = quote;

  if (
    type === "COM" ||
    type === "CUR" ||
    type === "GOV" ||
    type === "INDC" ||
    type === "INDF" ||
    type === "OEFH"
  ) {
    return false;
  }

  return true;
};

export const showOverview = (quote: QuoteData) => {
  const { type } = quote;

  return !(type === "OEFH" || type === "INDC");
};

// TODO: Manual mapping for specific ticker - needs global solution for indexes, then remove this mapping
const SPECIAL_INDEX_TICKERS = new Set(["BEOMFVVE:IND"]);

export function isHighPrecisionTicker(type: string, id?: string): boolean {
  if (HIGH_PRECISION_SECURITY_TYPES.has(type)) return true;
  if (type === "IND" && id) return SPECIAL_INDEX_TICKERS.has(id);

  return false;
}

export const generateShortId = (length = 4) => {
  // generate random 4-char alphanumeric string
  return Math.random()
    .toString(36)
    .substring(2, 2 + length);
};
